import { useState, type FormEvent } from 'react'
import type { AthleteProfile } from '../../domain/types'
import { localDateKey } from '../../domain/calendar'
import { displayMass, massUnit, storeMass, type UnitSystem } from '../../domain/units'

export function AthleteSetupForm({
  initialProfile,
  initialWeight,
  submitLabel,
  onSave,
  unit = 'metric',
}: {
  initialProfile: AthleteProfile
  initialWeight: number
  submitLabel: string
  onSave: (profile: AthleteProfile, weight: number) => void
  unit?: UnitSystem
}) {
  const [profile, setProfile] = useState(initialProfile)
  const [weight, setWeight] = useState(initialWeight)
  const targets = profile.nutritionTargets

  const setTarget = (key: keyof AthleteProfile['nutritionTargets'], value: number) => {
    setProfile((current) => ({
      ...current,
      nutritionTargets: { ...current.nutritionTargets, [key]: value },
    }))
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onSave({ ...profile, onboardingComplete: true }, weight)
  }

  return (
    <form className="athlete-form" onSubmit={submit}>
      <div className="form-intro">
        <span className="eyebrow">BASE PERSONAL</span>
        <p>Solo métricas y adherencia. No necesitas escribir lo que comes.</p>
      </div>

      <div className="form-grid three">
        <label><span>Edad</span><input required type="number" inputMode="numeric" min="14" max="100" value={profile.age} onChange={(event) => setProfile({ ...profile, age: Number(event.target.value) })} /><small>años</small></label>
        <label><span>Estatura</span><input required type="number" inputMode="decimal" min="120" max="230" step=".5" value={profile.heightCm} onChange={(event) => setProfile({ ...profile, heightCm: Number(event.target.value) })} /><small>cm</small></label>
        <label><span>Peso</span><input required type="number" inputMode="decimal" min={displayMass(35, unit)} max={displayMass(250, unit)} step=".1" value={Number(displayMass(weight, unit).toFixed(1))} onChange={(event) => setWeight(storeMass(Number(event.target.value), unit))} /><small>{massUnit(unit)}</small></label>
      </div>
      <p className="field-note">Dejé 184 cm como punto medio de tu rango estimado de 182–185; puedes corregirlo aquí.</p>

      <label className="wide-field">
        <span>Inicio de esta etapa de dieta</span>
        <input required type="date" max={localDateKey()} value={profile.dietStartedOn} onChange={(event) => setProfile({ ...profile, dietStartedOn: event.target.value })} />
        <small>Con esto calculamos el día y la semana actuales.</small>
      </label>

      <div className="form-label"><span>Macros diarios actuales</span><small>Objetivos, no alimentos</small></div>
      <div className="form-grid macros">
        <label><span>Calorías</span><input required type="number" inputMode="numeric" min="800" max="7000" step="10" value={targets.calories} onChange={(event) => setTarget('calories', Number(event.target.value))} /><small>kcal</small></label>
        <label><span>Proteína</span><input required type="number" inputMode="numeric" min="40" max="400" value={targets.protein} onChange={(event) => setTarget('protein', Number(event.target.value))} /><small>g</small></label>
        <label><span>Carbos</span><input required type="number" inputMode="numeric" min="0" max="1000" value={targets.carbohydrates} onChange={(event) => setTarget('carbohydrates', Number(event.target.value))} /><small>g</small></label>
        <label><span>Grasa</span><input required type="number" inputMode="numeric" min="20" max="300" value={targets.fat} onChange={(event) => setTarget('fat', Number(event.target.value))} /><small>g</small></label>
      </div>

      <button className="primary-button" type="submit">{submitLabel}</button>
    </form>
  )
}
